import { isDefinedArray, isPositiveNumber, reduce } from "et3-type-guards";
import { memoize, orderBy } from "lodash";
import { computed, observable, runInAction } from "mobx";
import { TGetKhattabaClients, TMatchesConversations } from "../../Types";
import getKhattabatSelectsStore from "./selects-store";
import {
  T101CFilters,
  T101MFilters,
  TEt3PickerMultipleValue
} from "../../TypesNEW";
import getKhattabatStore from ".";
import { customIntersectWithPriority } from "../../helpers/et3-data-helper";
import { getKhattabatResultsStore } from "./results-store";

class KhattabatFilterStore {
  filteredArray = observable.box<TGetKhattabaClients[]>([]);
  filtered101MArray = observable.box<TMatchesConversations>([]);
  searchingClients = observable.box<boolean>(false);
  searching1o1MMatches = observable.box<boolean>(false);
  filterSelectedTypes = observable.box<TEt3PickerMultipleValue>([]);
  filter1o1MSelectedTypes = observable.box<TEt3PickerMultipleValue>([]);

  setSearchingClients = (state: boolean) => {
    getKhattabatSelectsStore().initializeAndClearMarksStateWithClients(
      "clientPage"
    );
    runInAction(() => {
      this.searchingClients.set(state);
    });
  };

  setFilteredArray = (clients: TGetKhattabaClients[]) => {
    runInAction(() => {
      this.filteredArray.set(clients);
    });
  };

  setSearching1o1MMatches = (state: boolean) => {
    getKhattabatSelectsStore().initializeAndClearMarksStateWithClients(
      "matchResultPage"
    );
    runInAction(() => {
      this.searching1o1MMatches.set(state);
    });
  };

  setFilterSelectedTypes = (types: TEt3PickerMultipleValue) => {
    runInAction(() => this.filterSelectedTypes.set(types));
  };

  setFiltered1o1MArray = (clients: TMatchesConversations) => {
    runInAction(() => {
      this.filtered101MArray.set(clients);
    });
  };

  isActiveFilter = computed(() => {
    return isDefinedArray(this.filterSelectedTypes.get());
  });

  setFilterSelected101MTypes = (types: TEt3PickerMultipleValue) => {
    runInAction(() => this.filter1o1MSelectedTypes.set(types));
  };

  isActive1o1MFilter = computed(() => {
    return isDefinedArray(this.filter1o1MSelectedTypes.get());
  });

  isFilterCritereaHasBeenChosen = (criteria: T101CFilters) => {
    const currentSlectedCriterias = reduce(
      () => this.filterSelectedTypes.get(),
      []
    );
    return currentSlectedCriterias.some((selectedCriteria) => {
      return selectedCriteria.value === criteria;
    });
  };

  is1o1MFilterCritereaHasBeenChosen = (criteria: T101MFilters) => {
    const currentSelectedCriterias = reduce(
      () => this.filter1o1MSelectedTypes.get(),
      []
    );
    return currentSelectedCriterias.some((selectedCriteria) => {
      return selectedCriteria.value === criteria;
    });
  };

  handleFiltering = () => {
    const clients = getKhattabatStore().clients.get();

    //unread messages part
    let clientIdsForUnreadMessages: number[] =
      getKhattabatStore().allAvailableClientsIds.get();
    const isUnreadMessagesPresentInFilter =
      this.isFilterCritereaHasBeenChosen("unread_messages");
    if (isUnreadMessagesPresentInFilter) {
      clientIdsForUnreadMessages = clients.map((client) => {
        const haveUnreadMessages = isPositiveNumber(
          client.unreadOneOOneCMessages
        );
        if (haveUnreadMessages) {
          return client.userID;
        }
      });
    }

    //no 102
    let clientIdsForNo102: number[] =
      getKhattabatStore().allAvailableClientsIds.get();
    const isNoActive102PresentInFilter =
      this.isFilterCritereaHasBeenChosen("no_one_o_two");
    if (isNoActive102PresentInFilter) {
      clientIdsForNo102 = clients.map((client) => {
        const reducedOneOTwoCounter = reduce(() => client.oneOTwoCounter, 0);
        const reducedHasActiveOneOTwo = reduce(
          () => client.hasActiveOneOTwo,
          false
        );
        if (reducedOneOTwoCounter === 0 || !reducedHasActiveOneOTwo) {
          return client.userID;
        }
      });
    }

    //expired Subscription
    let clientIdsForExpiredSubscription: number[] =
      getKhattabatStore().allAvailableClientsIds.get();
    const isExpiredSubscriptionInFilter =
      this.isFilterCritereaHasBeenChosen("expired");
    if (isExpiredSubscriptionInFilter) {
      clientIdsForExpiredSubscription = clients.map((client) => {
        const reducedClientSubscription = reduce(
          () => client.vipAlkhattabatStatus,
          "EXPIRED"
        );
        if (reducedClientSubscription === "EXPIRED") {
          return client.userID;
        }
      });
    }

    // is renewed is last 7 days
    let clientIdsForRenewedLastSevenDays: number[] =
      getKhattabatStore().allAvailableClientsIds.get();
    const isRenewedLastSevenDaysInFilter =
      this.isFilterCritereaHasBeenChosen("renewed");
    if (isRenewedLastSevenDaysInFilter) {
      clientIdsForRenewedLastSevenDays = clients.map((client) => {
        const isRenewedInLastSevenDays = reduce(
          () => client.isRenewedInLastSevenDays,
          false
        );
        if (isRenewedInLastSevenDays) {
          return client.userID;
        }
      });
    }

    //intersection part
    const intersectionArray = customIntersectWithPriority(
      [
        clientIdsForUnreadMessages,
        clientIdsForNo102,
        clientIdsForExpiredSubscription,
        clientIdsForRenewedLastSevenDays
      ],
      []
    );

    const filteredClients = intersectionArray
      .map((id) =>
        getKhattabatStore()
          .clients.get()
          .find((client) => client.userID === id)
      )
      .filter(Boolean); // Removes any undefined values if a client isn't found
    this.setFilteredArray(filteredClients);
    this.setSearchingClients(true);
  };

  handle1o1MFiltering = () => {
    const matches = getKhattabatResultsStore().matchesConversations.get();

    //unread messages part
    let matchesIdsForUnreadMessages: number[] =
      getKhattabatResultsStore().getAllAvailableMatchesIds.get();
    const isUnreadMessagesPresentInFilter =
      this.is1o1MFilterCritereaHasBeenChosen("unread_messages");
    if (isUnreadMessagesPresentInFilter) {
      matchesIdsForUnreadMessages = matches.map((matchDetails) => {
        const haveUnreadMessages = isPositiveNumber(
          matchDetails.matchUnreadMessages
        );
        if (haveUnreadMessages) {
          return matchDetails.matchID;
        }
      });
    }

    // one o one m highlighted
    let matchesIdsForOneOOneMHighlighted: number[] =
      getKhattabatResultsStore().getAllAvailableMatchesIds.get();
    const isOneOOneMHighlightedInFilter =
      this.is1o1MFilterCritereaHasBeenChosen("highlighted");
    if (isOneOOneMHighlightedInFilter) {
      matchesIdsForOneOOneMHighlighted = matches.map((matchDetails) => {
        const isOneOOneMHighlighted =
          matchDetails.matchConversationIsHighlighted;
        if (isOneOOneMHighlighted) {
          return matchDetails.matchID;
        }
      });
    }

    // last login desc
    let matchesIdsForLastLoginDescending: number[] =
      getKhattabatResultsStore().getAllAvailableMatchesIds.get();
    const matchesIdsForLastLoginDescendingInFilter =
      this.is1o1MFilterCritereaHasBeenChosen("recent_login");
    if (matchesIdsForLastLoginDescendingInFilter) {
      const orderedMatches = orderBy(matches, ["matchLastLogin"], ["desc"]);
      matchesIdsForLastLoginDescending = orderedMatches.map((matchDetails) => {
        return matchDetails.matchID;
      });
    }

    //intersection part
    const intersectionArray = customIntersectWithPriority(
      [
        matchesIdsForUnreadMessages,
        matchesIdsForOneOOneMHighlighted,
        matchesIdsForLastLoginDescending
      ],
      matchesIdsForLastLoginDescendingInFilter
        ? matchesIdsForLastLoginDescending
        : []
    );
    let filteredMatches: TMatchesConversations = [];
    if (isDefinedArray(intersectionArray)) {
      intersectionArray.forEach((matchID) => {
        const matchIndexInOriginalArray =
          getKhattabatResultsStore().findMatchIndexInConversationArray({
            parameter: "matchID",
            parameterValue: matchID,
            matchesConversations:
              getKhattabatResultsStore().matchesConversations.get()
          });

        if (isPositiveNumber(matchIndexInOriginalArray, true)) {
          filteredMatches.push(
            getKhattabatResultsStore().matchesConversations.get()[
              matchIndexInOriginalArray
            ]
          );
        }
      });
    }

    this.setFiltered1o1MArray(filteredMatches);
    this.setSearching1o1MMatches(true);
  };

  resetFilters = () => {
    this.setFilterSelectedTypes([]);
    this.setFilteredArray([]);
    this.setSearchingClients(false);
    getKhattabatSelectsStore().initializeAndClearMarksStateWithClients(
      "clientPage"
    );
  };

  reset1o1MFilters = () => {
    this.setFilterSelected101MTypes([]);
    this.setFiltered1o1MArray([]);
    this.setSearching1o1MMatches(false);
    getKhattabatSelectsStore().initializeAndClearMarksStateWithClients(
      "matchResultPage"
    );
  };

  isSearchingClients = computed(() => {
    return this.searchingClients.get() || this.isActiveFilter.get();
  });

  isSearching1o1MMatches = computed(() => {
    return this.searching1o1MMatches.get() || this.isActive1o1MFilter.get();
  });

  update1o1MFilter = () => {
    const isActiveFilter =
      getKhattabatFilterstore().isSearching1o1MMatches.get();

    if (isActiveFilter) {
      getKhattabatFilterstore().handle1o1MFiltering();
    }
  };
}

export const getKhattabatFilterstore = memoize(
  () => {
    return new KhattabatFilterStore();
  },
  () => 1
);

export default getKhattabatFilterstore;
